import { ApiMethodType, OperatorsEnum } from '@celito.clients/enums';
import {
  getObjectData,
  getObjectMetadata,
} from '@celito.clients/list-view-engine';
import { performRequest } from '@celito.clients/network';
import { BaseEntityWithName } from 'apps/web-client/src/types/base-entity';
import Endpoints from 'libs/core/src/config/endpoint';
import { MappingTypeEnum } from 'libs/form-engine/src/lib/components/form-components/controlled-picker/controlled-picker.model';
import { useQuery } from 'react-query';

export interface IInboudRelation {
  label: string;
  ownerType: string;
  name: string;
  createdAtUtc: Date;
  modifiedAtUtc: Date | null;
  deletedAtUtc: Date | null;
  isActive: boolean;
  relationType: string;
  objectName: string;
  deletionType: string;
  destinationColumnName: string;
  sourceColumnName: string;
  objectMappingType: string;
}

export function useGetInboundRelationship(
  objectName?: string,
  enabled?: boolean
) {
  return useQuery(
    ['object-inbound-relationship', objectName],
    () => fetchInboundRelationship(objectName),
    {
      enabled,
    }
  );
}

export function useGetInboundRelatedRecords(
  parentObjectName?: string,
  recordName?: string,
  enabled?: boolean
) {
  const fetchRecords = async () => {
    if (!parentObjectName || !recordName) {
      return [] as BaseEntityWithName[];
    }

    const objectMetadata = await getObjectMetadata(parentObjectName);

    if (!objectMetadata?.relatedObjectAuditLog) {
      return [] as BaseEntityWithName[];
    }

    const inboundRelationships =
      await fetchInboundRelationship(parentObjectName);

    const records = await fetchInboundRelatedRecords({
      inboundRelationships,
      recordName,
    });

    return records;
  };

  return useQuery(
    ['inbound-related-records', parentObjectName, recordName],
    fetchRecords,
    {
      enabled: enabled && !!parentObjectName && !!recordName,
    }
  );
}

export async function fetchInboundRelationship(objectName?: string) {
  if (!objectName) return [] as IInboudRelation[];

  const response = await performRequest<IInboudRelation[]>(
    `${Endpoints.OBJECT_DEFINITION_URL}/${objectName}/inbound-relationships`,
    ApiMethodType.GET
  );

  return response.data;
}

export async function fetchInboundRelatedRecords({
  inboundRelationships,
  recordName,
}: {
  inboundRelationships: IInboudRelation[];
  recordName: string;
}) {
  const relatedChildRecordsPromises = inboundRelationships.map(
    async (inboundRelationship) => {
      if (inboundRelationship.objectMappingType !== MappingTypeEnum.OneToMany) {
        return [];
      }

      const data = await getObjectData<BaseEntityWithName>(
        inboundRelationship.objectName,
        {
          limit: 50,
          page: 1,
        },
        {
          filters: {
            conditions: {
              any: [
                {
                  all: [
                    {
                      attribute: inboundRelationship.destinationColumnName,
                      operator: OperatorsEnum.EQUALS,
                      value: recordName,
                    },
                  ],
                },
              ],
            },
          },
        }
      );

      return data?.data || [];
    }
  );

  const relatedChildRecords = await Promise.all(relatedChildRecordsPromises);
  return relatedChildRecords.length > 0 ? relatedChildRecords.flat() : [];
}
